import { Icon, Logo } from "@caint/design-system";
import { Fragment, useCallback, useState } from "react";
import "../desktop/desktop.css";
import { AssinaturaCampos } from "../components/AssinaturaCampos.tsx";
import { MatrizUpload } from "../components/MatrizUpload.tsx";
import { ResultadosPublico } from "../components/ResultadosPublico.tsx";
import { SeletorCicloDesktop, SemCicloSelecionado } from "../components/SeletorCicloGlobal.tsx";
import { SupervisaoChecklist } from "../components/SupervisaoChecklist.tsx";
import { TrocarSenhaCampos } from "../components/TrocarSenhaCampos.tsx";
import { RelatorioUnidadeInterno } from "../components/relatorio/RelatorioUnidadeInterno.tsx";
import { ApresentacaoDesktop } from "../desktop/screens/Apresentacao.tsx";
import { Auditoria } from "../desktop/screens/Auditoria.tsx";
import { ChecklistDesktop } from "../desktop/screens/Checklist.tsx";
import { CiclosDesktop } from "../desktop/screens/Ciclos.tsx";
import { InicioDesktop } from "../desktop/screens/Inicio.tsx";
import { LoginDesktop } from "../desktop/screens/Login.tsx";
import { MigracaoWorkbench } from "../desktop/screens/Migracao.tsx";
import { PanoramaDesktop } from "../desktop/screens/Panorama.tsx";
import { PlanoAcaoDesktop } from "../desktop/screens/PlanoAcao.tsx";
import { RelatorioDesktop } from "../desktop/screens/Relatorio.tsx";
import { RiscosDesktop } from "../desktop/screens/Riscos.tsx";
import { UsuariosDesktop } from "../desktop/screens/Usuarios.tsx";
import * as Ref from "../desktop/screens/referencia.tsx";
import { Modal, PageHeader } from "../desktop/ui/kit.tsx";
import { exigeCicloSelecionado, podeGerirCiclo } from "../lib/ciclos.ts";
import { GRUPOS_NAV, type GrupoNav, type ItemNav, filtrarGrupos, gruposVisiveis } from "../lib/navegacao.ts";
import { useAssinatura } from "../lib/useAssinatura.ts";
import { useTrocarSenha } from "../lib/useTrocarSenha.ts";
import { PortalUnidade } from "../portal/PortalUnidade.tsx";
import { ChecklistImpressao } from "../print/ChecklistImpressao.tsx";
import { SupervisaoImpressao } from "../print/SupervisaoImpressao.tsx";
import { EscopoSetores } from "../screens/EscopoSetores.tsx";
import {
  type Aba,
  type AppState,
  type QuadroPilha,
  type UsuarioAutenticado,
  useAppState,
} from "../state/useAppState.ts";

function TrocarSenhaModal({
  trocarSenha,
  onClose,
}: {
  trocarSenha: (senhaAtual: string, novaSenha: string) => Promise<void>;
  onClose: () => void;
}) {
  const f = useTrocarSenha(trocarSenha);
  return (
    <Modal
      titulo="Trocar senha"
      sub="Atualize a senha da sua conta"
      onClose={onClose}
      foot={
        f.sucesso ? (
          <button type="button" className="m-btn primary" onClick={onClose}>
            Concluído
          </button>
        ) : (
          <button
            type="button"
            className="m-btn primary"
            disabled={f.enviando}
            onClick={() => void f.submeter()}
          >
            {f.enviando ? "Salvando…" : "Salvar nova senha"}
          </button>
        )
      }
    >
      <TrocarSenhaCampos f={f} />
    </Modal>
  );
}

function AssinaturaModal({ token, onClose }: { token: string | null; onClose: () => void }) {
  const f = useAssinatura(token);
  return (
    <Modal
      titulo="Minha assinatura"
      sub="Imagem que aparece sobre a linha no Relatório da Unidade"
      onClose={onClose}
      foot={
        <button type="button" className="m-btn" onClick={onClose}>
          Fechar
        </button>
      }
    >
      <AssinaturaCampos f={f} />
    </Modal>
  );
}

const SUBTELAS: Record<string, () => React.ReactNode> = {
  monitoramento: Ref.Monitoramento,
  percepcao: Ref.Percepcao,
  supervisao: Ref.Supervisao,
  catalogo: Ref.Catalogo,
  matriz: Ref.Matriz,
  motor: Ref.Motor,
  escalas: Ref.Escalas,
  estrutura: Ref.Estrutura,
  versoes: Ref.Versoes,
  imprimir: () => <ChecklistImpressao />,
};

const TITULO_ROTA: Record<string, string> = {
  unit: "Painel da Unidade",
  contextuais: "Riscos Contextuais",
  plano: "Plano de Ação",
};

function DesktopEmBreve({ titulo }: { titulo: string }) {
  return (
    <div className="dk-page">
      <PageHeader titulo={titulo} sub="Tela em construção nesta versão" />
      <p className="spec">Disponível em breve.</p>
    </div>
  );
}

function DesktopSemCiclo({ podeAbrir, irCiclos }: { podeAbrir: boolean; irCiclos: () => void }) {
  return (
    <div className="dk-page">
      <PageHeader titulo="Nenhum ciclo aberto" sub="Aguardando abertura de ciclo pela presidência" />
      <div className="dk-card">
        <p style={{ color: "var(--text-muted)", margin: 0 }}>
          As avaliações começam quando o presidente abre um ciclo para uma unidade.
        </p>
        {podeAbrir && (
          <button type="button" className="m-btn primary" style={{ marginTop: 14 }} onClick={irCiclos}>
            Abrir ciclo
          </button>
        )}
      </div>
    </div>
  );
}

/** Supervisão no desktop: audin + gestão (admin/coord/presidente) veem o
 * checklist; os demais papéis, o overview (doc 09). */
function SupervisaoDesktop({ s }: { s: AppState }) {
  if (!s.podeSupervisionar) {
    return <Ref.Supervisao />;
  }
  return (
    <div className="dk-page">
      <PageHeader titulo="Supervisão AUDIN" sub="Asseguração da 3ª linha de defesa" />
      <SupervisaoChecklist
        dados={s.supervisao}
        podeEditar={s.podeEditarSupervisao}
        editar={s.editarSupervisao}
        enviar={s.enviarSupervisao}
        itens={s.itensEscopoSup}
        amostraAlvo={s.amostraAlvo}
        checklistCompleto={s.checklistCompleto}
        liberarAmostra={s.liberarAmostra}
        podeLiberarAmostra={s.podeLiberarAmostra}
        online={s.online}
        onImprimir={() => s.go("imprimir-sup")}
      />
    </div>
  );
}

/** Tela de escopo (Setores da Unidade) — pré-avaliação e reabertura (doc 17b §5). */
function montarEscopoDesktop(s: AppState, onPronto: () => void): React.ReactNode {
  return (
    <div className="dk-page">
      <EscopoSetores
        escopo={s.escopo}
        items={s.items}
        alternarSetor={s.alternarSetor}
        confirmarEscopo={s.confirmarEscopo}
        editavel={s.editavel}
        onPronto={onPronto}
        unidadeId={s.avaliacaoAtual?.unidadeId}
      />
    </div>
  );
}

/** Resolve a tela de uma rota empilhada no desktop (sidebar "Mais"). */
function resolverPilhaDesktop(
  top: QuadroPilha,
  s: AppState,
  auth: UsuarioAutenticado,
  checklist: React.ReactNode,
): React.ReactNode {
  switch (top.route) {
    case "checklist":
      return checklist;
    case "escopo":
      return montarEscopoDesktop(s, s.back);
    case "apresentacao":
      return (
        <ApresentacaoDesktop
          items={s.itensEscopo}
          momento={s.momento}
          imgq={s.imgq}
          qaci={s.qaci}
          ims={s.ims}
          avaliacaoAtual={s.avaliacaoAtual}
          evolucao={s.evolucao}
        />
      );
    case "panorama":
      return <PanoramaDesktop panorama={s.panoramaRede} />;
    case "usuarios":
      return <UsuariosDesktop papel={auth.papel} api={s.usuariosApi} euId={auth.id} />;
    case "auditoria":
      return <Auditoria token={s.token} />;
    case "relatorio":
      return (
        <RelatorioDesktop
          items={s.itensEscopo}
          momento={s.momento}
          qaci={s.qaci}
          ims={s.ims}
          avaliacaoAtual={s.avaliacaoAtual}
          token={s.token}
          evolucao={s.evolucao}
          supervisao={s.supervisao}
          carregarImgr={s.ciclosApi.resultado}
        />
      );
    case "relatorio-unidade":
      return <RelatorioUnidadeInterno avaliacaoId={s.avaliacaoAtual?.id ?? null} token={s.token} />;
    case "matriz-unidade":
      return (
        <div className="dk-page">
          <MatrizUpload matrizApi={s.matrizApi} />
        </div>
      );
    case "ciclos":
      return (
        <CiclosDesktop
          ciclosApi={s.ciclosApi}
          recarregar={s.recarregar}
          podeAbrir={podeGerirCiclo(auth.papel)}
          autor={auth.nome}
          avaliacaoIdAtivo={s.avaliacaoAtual?.id ?? null}
          onAtivar={s.selecionar}
        />
      );
    case "supervisao":
      return <SupervisaoDesktop s={s} />;
    case "migracao":
      return <MigracaoWorkbench api={s.migracaoApi} papel={auth.papel} autor={auth.nome} />;
    case "imgr":
      return (
        <div className="dk-page">
          <PageHeader titulo="IMGR" sub="Maturidade em gestão de riscos — formulário público" />
          <ResultadosPublico ciclosApi={s.ciclosApi} />
        </div>
      );
    case "planos":
      return (
        <div className="dk-page">
          <PlanoAcaoDesktop avaliacaoId={s.avaliacaoAtual?.id ?? null} token={s.token} onBack={s.back} />
        </div>
      );
    case "imprimir-sup":
      return (
        <SupervisaoImpressao
          amostra={s.supervisao.amostra}
          unidadeId={s.avaliacaoAtual?.unidadeId}
          supervisor={auth.nome}
        />
      );
    default: {
      const Sub = SUBTELAS[top.route];
      return Sub ? <Sub /> : <DesktopEmBreve titulo={TITULO_ROTA[top.route] ?? top.route} />;
    }
  }
}

function resolverTelaDesktop(
  s: AppState,
  auth: UsuarioAutenticado,
  toast: (m: string) => void,
): React.ReactNode {
  const podeAbrir = podeGerirCiclo(auth.papel);
  // Gate (doc 17b §3.2): o checklist só abre depois do escopo confirmado.
  const checklist = s.escopo.escopoConfirmado ? (
    <ChecklistDesktop
      items={s.items}
      momento={s.momento}
      setMomento={s.setMomento}
      patch={s.patch}
      toast={toast}
      evidencias={s.evidencias}
      evidenciasPendentes={s.evidenciasPendentes}
      enviarEvidencia={s.enviarEvidencia}
      excluirEvidencia={s.excluirEvidencia}
      substituirEvidencia={s.substituirEvidencia}
      removerEvidenciaPendente={s.removerEvidenciaPendente}
      editavel={s.editavel}
      avaliacaoAtual={s.avaliacaoAtual}
      setoresAtivos={s.escopo.setoresAtivos}
      onEditarEscopo={() => s.go("escopo")}
    />
  ) : (
    montarEscopoDesktop(s, () => {})
  );
  const top = s.stack[s.stack.length - 1];
  // Guardrail: nenhuma tela com escopo de ciclo abre sem ciclo selecionado — sem
  // isto ela parece editável e descarta todo o preenchimento (ver `SemCicloSelecionado`).
  const alvo = top?.route ?? s.tab;
  if (!s.avaliacaoAtual && s.avaliacoes.length > 0 && exigeCicloSelecionado(alvo)) {
    return (
      <div className="dk-page">
        <PageHeader titulo="Selecione um ciclo" sub={TITULO_ROTA[alvo] ?? "Ciclo não selecionado"} />
        <SemCicloSelecionado
          avaliacoes={s.avaliacoes}
          avaliacaoId={null}
          onSelecionar={s.selecionar}
          onGerir={() => s.go("ciclos")}
        />
      </div>
    );
  }
  if (top) {
    return resolverPilhaDesktop(top, s, auth, checklist);
  }
  if (s.avaliacoes.length === 0) {
    return <DesktopSemCiclo podeAbrir={podeAbrir} irCiclos={() => s.go("ciclos")} />;
  }
  switch (s.tab) {
    case "checklist":
      return checklist;
    case "risco":
      return <RiscosDesktop items={s.itensEscopo} momento={s.momento} />;
    case "relatorio":
      return (
        <RelatorioDesktop
          items={s.itensEscopo}
          momento={s.momento}
          qaci={s.qaci}
          ims={s.ims}
          avaliacaoAtual={s.avaliacaoAtual}
          token={s.token}
          evolucao={s.evolucao}
          supervisao={s.supervisao}
          carregarImgr={s.ciclosApi.resultado}
        />
      );
    default:
      return (
        <InicioDesktop
          items={s.itensEscopo}
          qaci={s.qaci}
          ims={s.ims}
          go={s.go}
          avaliacaoAtual={s.avaliacaoAtual}
          avaliacoes={s.avaliacoes}
          escopoConfirmado={s.escopo.escopoConfirmado}
          onAbrirCiclo={(id) => {
            s.selecionar(id);
            s.go("relatorio");
          }}
        />
      );
  }
}

/** Um grupo recolhível da sidebar com seus itens. */
function GrupoSidebar({
  g,
  open,
  bloqueado,
  onToggle,
  ativo,
  abrir,
}: {
  g: GrupoNav;
  open: boolean;
  bloqueado: boolean;
  onToggle: () => void;
  ativo: (it: ItemNav) => boolean;
  abrir: (it: ItemNav) => void;
}) {
  return (
    <Fragment>
      <button
        type="button"
        className={`dk-group${open ? "" : " closed"}`}
        disabled={bloqueado}
        onClick={onToggle}
      >
        <span className="dk-gname">{g.nome}</span>
        <span className="dk-gcount">{g.itens.length}</span>
        <Icon name="chevron" size={13} />
      </button>
      {open &&
        g.itens.map((it) => {
          const on = ativo(it);
          return (
            <button
              key={it.rota}
              type="button"
              className={`dk-item${on ? " on" : ""}`}
              onClick={() => abrir(it)}
              title={it.sub}
            >
              <span className="dk-ico" style={on ? undefined : { color: g.cor }}>
                <Icon name={it.icone} size={18} />
              </span>
              <span className="dk-lbl">{it.titulo}</span>
            </button>
          );
        })}
    </Fragment>
  );
}

const CHAVE_RAIL = "caint-dk-rail";

function Sidebar({ s, auth }: { s: AppState; auth: UsuarioAutenticado }) {
  const top = s.stack[s.stack.length - 1];
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarAssinatura, setMostrarAssinatura] = useState(false);
  const [busca, setBusca] = useState("");
  const [rail, setRail] = useState<boolean>(() => {
    try {
      return localStorage.getItem(CHAVE_RAIL) === "1";
    } catch {
      return false;
    }
  });
  const [aberto, setAberto] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(GRUPOS_NAV.map((g) => [g.id, true])),
  );
  const buscando = busca.trim().length > 0;
  const grupos = filtrarGrupos(gruposVisiveis(GRUPOS_NAV, auth.papel), busca);

  const alternarRail = () =>
    setRail((r) => {
      const novo = !r;
      try {
        localStorage.setItem(CHAVE_RAIL, novo ? "1" : "0");
      } catch {
        /* armazenamento indisponível */
      }
      return novo;
    });

  // Avaliação são abas (switchTab); os demais grupos empilham rota (go).
  const abrir = (it: ItemNav) => {
    if (GRUPOS_NAV[0]?.itens.some((x) => x.rota === it.rota)) {
      s.switchTab(it.rota as Aba);
    } else {
      s.switchTab(s.tab);
      s.go(it.rota);
    }
  };
  const ativo = (it: ItemNav) =>
    GRUPOS_NAV[0]?.itens.some((x) => x.rota === it.rota) ? !top && s.tab === it.rota : top?.route === it.rota;

  return (
    <aside className={`dk-side${rail ? " rail" : ""}`}>
      <div className="dk-brand">
        <Logo size={26} />
        <b className="dk-lbl">CAINT</b>
        <button
          type="button"
          className="dk-collapse"
          title={rail ? "Expandir menu" : "Recolher menu"}
          onClick={alternarRail}
        >
          <Icon name="panel" size={17} />
        </button>
      </div>
      {!rail && (
        <SeletorCicloDesktop
          avaliacoes={s.avaliacoes}
          avaliacaoId={s.avaliacaoAtual?.id ?? null}
          onSelecionar={s.selecionar}
          onGerir={() => {
            s.switchTab(s.tab);
            s.go("ciclos");
          }}
        />
      )}
      {!rail && (
        <div className="dk-search">
          <Icon name="search" size={15} />
          <input
            type="search"
            placeholder="Buscar no menu…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
      )}
      <nav className="dk-nav">
        {grupos.map((g) => (
          <GrupoSidebar
            key={g.id}
            g={g}
            open={rail || buscando ? true : (aberto[g.id] ?? true)}
            bloqueado={rail || buscando}
            onToggle={() => setAberto((a) => ({ ...a, [g.id]: !(a[g.id] ?? true) }))}
            ativo={ativo}
            abrir={abrir}
          />
        ))}
        {grupos.length === 0 && <div className="dk-empty">Nada encontrado para “{busca.trim()}”.</div>}
      </nav>
      <div className="dk-foot">
        <div className="dk-user">{auth.nome}</div>
        <div className="spec dk-lbl" style={{ padding: "0 8px" }}>
          {auth.funcao} · {s.online ? "online" : "offline"}
          {s.syncStatus.estado === "sincronizando"
            ? " · sincronizando…"
            : s.pendingSync
              ? ` · ${s.pendingSync} na fila`
              : ""}
        </div>
        {s.pendingSync > 0 && s.online && (
          <button
            type="button"
            className="dk-item"
            title="Sincronizar agora"
            disabled={s.syncStatus.estado === "sincronizando"}
            onClick={() => void s.sync()}
          >
            <span className="dk-ico">
              <Icon name="clock" size={18} />
            </span>
            <span className="dk-lbl">
              {s.syncStatus.estado === "sincronizando" ? "Sincronizando…" : "Sincronizar"}
            </span>
          </button>
        )}
        {(s.syncStatus.estado === "erro" || s.syncStatus.estado === "retido") && s.syncStatus.mensagem && (
          <div
            className="spec dk-lbl"
            style={{
              padding: "0 8px",
              color: s.syncStatus.estado === "erro" ? "var(--danger)" : "var(--warning)",
            }}
            role="alert"
          >
            {s.syncStatus.estado === "erro" ? "⚠️" : "⏳"} {s.syncStatus.mensagem}
          </div>
        )}
        {s.temRespostasLocais && s.online && (
          <button
            type="button"
            className="dk-item"
            title="Reenvia ao servidor todas as respostas deste ciclo salvas neste aparelho (recuperação)"
            disabled={s.syncStatus.estado === "sincronizando"}
            onClick={() => s.reenviarRespostasLocais()}
          >
            <span className="dk-ico">
              <Icon name="clock" size={18} />
            </span>
            <span className="dk-lbl">Reenviar respostas do ciclo</span>
          </button>
        )}
        <button type="button" className="dk-item" title="Trocar senha" onClick={() => setMostrarSenha(true)}>
          <span className="dk-ico">
            <Icon name="gear" size={18} />
          </span>
          <span className="dk-lbl">Trocar senha</span>
        </button>
        <button
          type="button"
          className="dk-item"
          title="Minha assinatura"
          onClick={() => setMostrarAssinatura(true)}
        >
          <span className="dk-ico">
            <Icon name="doc" size={18} />
          </span>
          <span className="dk-lbl">Minha assinatura</span>
        </button>
        <button
          type="button"
          className="dk-item"
          style={{ color: "var(--danger)" }}
          title="Sair"
          onClick={s.logout}
        >
          <span className="dk-ico">
            <Icon name="logout" size={18} />
          </span>
          <span className="dk-lbl">Sair</span>
        </button>
      </div>
      {mostrarSenha && (
        <TrocarSenhaModal trocarSenha={s.trocarSenha} onClose={() => setMostrarSenha(false)} />
      )}
      {mostrarAssinatura && <AssinaturaModal token={s.token} onClose={() => setMostrarAssinatura(false)} />}
    </aside>
  );
}

export function AppDesktop() {
  const s = useAppState();
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const toast = useCallback((msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 2400);
  }, []);

  if (s.unidadeSession) {
    // Conta de unidade: login único → só a view do Plano de Ação.
    return (
      <PortalUnidade
        token={s.unidadeSession.token}
        nome={s.unidadeSession.nome}
        cargo={s.unidadeSession.cargo}
        onSair={s.logout}
      />
    );
  }

  if (!s.auth) {
    return (
      <LoginDesktop
        onSignIn={s.signIn}
        aviso={s.sessaoExpirada ? "Sua sessão expirou. Entre novamente para continuar." : undefined}
      />
    );
  }

  return (
    <div className="dk-root">
      <Sidebar s={s} auth={s.auth} />
      <main className="dk-main">
        {resolverTelaDesktop(s, s.auth, toast)}
        {toastMsg && <div className="m-toast">{toastMsg}</div>}
      </main>
    </div>
  );
}
